import { Form, Formik } from "formik";
import { useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from "@material-ui/core";

import { useTenant } from "src/components/LoggedInAdminProvider";
import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import FormikTextField from "src/components/form/FormikTextField";
import { createBlockedPhoneNumberVariables } from "src/graphql/generated/createBlockedPhoneNumber";
import { getErrorMessage } from "src/util/errors";
import yup from "src/util/yup";

export type AddBlockedPhoneNumberValues = Omit<
  createBlockedPhoneNumberVariables,
  "subaccountId"
>;

export default function AddBlockedPhoneNumberDialog({
  open,
  onClose,
  onSubmit
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: AddBlockedPhoneNumberValues) => Promise<void>;
}): JSX.Element {
  const tenant = useTenant();
  const [saveErrors, setSaveErrors] = useState<string | null>(null);

  const handleSubmit = async (payload: AddBlockedPhoneNumberValues) => {
    setSaveErrors(null);

    try {
      await onSubmit(payload);
    } catch (e) {
      setSaveErrors(getErrorMessage(e));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Block a Caller</DialogTitle>
      <Formik
        initialValues={{ phoneNumber: "", note: "" }}
        validationSchema={yup.object().shape({
          phoneNumber: yup
            .string()
            .required("Required")
            .phoneNumber(tenant?.countryIso),
          note: yup.string()
        })}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogContent>
              <FormikErrorBanner
                renderBanner={errors => {
                  if (errors.saveErrors) {
                    return `Error saving: ${errors.saveErrors}`;
                  }
                  return "Please fix the errors indicated below.";
                }}
                additionalErrors={saveErrors}
              />
              <DialogContentText>
                This number will no longer be able to verify and join calling
                sessions. Anyone already calling from it is signed out
                immediately.
              </DialogContentText>
              <FormikTextField
                fullWidth
                name="phoneNumber"
                label="Phone number"
              />
              <FormikTextField
                fullWidth
                name="note"
                label="Note (optional)"
                helperText="Why this number was blocked, for whoever reviews it later"
              />
            </DialogContent>
            <DialogActions>
              <Button disabled={isSubmitting} onClick={onClose} color="primary">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                color="primary"
                variant="contained"
              >
                {isSubmitting ? "Saving..." : "Block"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
